import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ModalController, PickerController } from '@ionic/angular';
import { takeUntil } from 'rxjs/operators';
import * as v4 from 'uuid/v4';
import { Assessment } from '@haleo-frontend/data-access/models';
import { AbstractComponent, AssessmentUtils } from '@haleo-frontend/utils';
import { AssessmentService } from '@haleo-frontend/data-access/services';
import { PickerSelectOverlayComponent, PickerTimeOverlayComponent } from '@haleo-frontend/ui';

@Component({
    selector: 'haleo-assessment-error',
    templateUrl: 'error.component.html',
    styleUrls: ['error.component.scss']
})
export class ErrorComponent extends AbstractComponent implements OnChanges {

    @Input('assessment') partialAssessment: Assessment;
    @Input() type: string;
    @Input() app: string;
    @Output() fixed: EventEmitter<void>;

    assessment: Assessment;
    sleepEfficiencyType = 'sleepEfficiency';
    timeInBedType = 'timeInBed';
    timeError: string;
    wokeUpAt: string;
    inBedAt: string;
    timeAwake: string;
    timeToSleep: string;
    timeToGetOutBed: string;
    loading: boolean;
    enableNext: boolean;

    constructor(private assessmentService: AssessmentService,
                private translateService: TranslateService,
                private pickerCtrl: PickerController,
                private modalController: ModalController) {
        super();
        this.fixed = new EventEmitter<void>();
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (this.partialAssessment) {
            this.loading = true;
            this.init();
        }
    }

    async answerInBedAt() {
        if (!this.loading) {
            if (this.app === 'haleo') {
                const question = this.assessment.questions.find(q => q.key === 'SD_Q5' || q.key === 'SD_Q5_SW');

                if (question) {
                    this.sendAnswer(question.key, this.inBedAt);
                }
            } else {
                await this.openNumberPickerSelfGuided('SD_Q5');
            }
        }
    }

    async answerWokeUpAt() {
        if (!this.loading) {
            if (this.app === 'haleo') {
                const question = this.assessment.questions.find(q => q.key === 'SD_Q1' || q.key === 'SD_Q1_SW');

                if (question) {
                    this.sendAnswer(question.key, this.wokeUpAt);
                }
            } else {
                await this.openNumberPickerSelfGuided('SD_Q1');
            }
        }
    }

    async openNumberPickerTimeToSleep() {
        if (!this.loading) {
            if (this.app === 'haleo') {
                this.timeToSleep = await this.openNumberPickerHaleo('SD_Q6');
                this.sendAnswer('SD_Q6', this.timeToSleep);
            } else {
                await this.openNumberPickerSelfGuided('SD_Q6');
            }
        }
    }

    async openNumberPickerTimeAwake() {
        if (!this.loading) {
            if (this.app === 'haleo') {
                this.timeAwake = await this.openNumberPickerHaleo('SD_Q7.1');
                this.sendAnswer('SD_Q7.1', this.timeAwake);
            } else {
                await this.openNumberPickerSelfGuided('SD_Q7.1');
            }
        }
    }

    async openNumberPickerTimeToGetOutBed() {
        if (!this.loading) {
            if (this.app === 'haleo') {
                this.timeToGetOutBed = await this.openNumberPickerHaleo('SD_Q15');
                this.sendAnswer('SD_Q15', this.timeToGetOutBed);
            } else {
                await this.openNumberPickerSelfGuided('SD_Q15');
            }
        }
    }

    next() {
        this.loading = true;
        this.fixed.emit();
    }

    private init() {
        this.assessmentService.getAssessment(this.partialAssessment.uuid)
            .pipe(takeUntil(this.destroy))
            .subscribe(assessment => {

                this.assessment = assessment;
                let timeErrorValue;

                if (this.type === this.sleepEfficiencyType) {
                    timeErrorValue = AssessmentUtils.getTST(this.assessment);
                } else {
                    timeErrorValue = AssessmentUtils.getTIB(this.assessment);
                }

                if (timeErrorValue) {

                    if (Math.abs(timeErrorValue) < 60) {
                        this.timeError = this.translateService.instant('assessment.error.negativeTime', {mins: timeErrorValue});
                    } else {

                        let hours;
                        if (timeErrorValue < 0) {
                            hours = Math.ceil(timeErrorValue / 60);
                        } else {
                            hours = Math.floor(timeErrorValue / 60);
                        }

                        const mins = timeErrorValue % 60;

                        this.timeError = this.translateService.instant('assessment.error.negativeTimeHour', {
                            hours,
                            mins
                        });
                    }
                }

                this.inBedAt = AssessmentUtils.getAnswerValueMultipleKeys(['SD_Q5', 'SD_Q5_SW'], this.assessment.questions, this.assessment.answers);
                this.timeToSleep = AssessmentUtils.getAnswerValue('SD_Q6', this.assessment.questions, this.assessment.answers);
                this.timeAwake = AssessmentUtils.getAnswerValue('SD_Q7.1', this.assessment.questions, this.assessment.answers);
                this.wokeUpAt = AssessmentUtils.getAnswerValueMultipleKeys(['SD_Q1', 'SD_Q1_SW'], this.assessment.questions, this.assessment.answers);
                this.timeToGetOutBed = AssessmentUtils.getAnswerValue('SD_Q15', this.assessment.questions, this.assessment.answers);

                this.loading = false;
            });
    }

    private async openNumberPickerSelfGuided(questionKey: string) {

        const question = this.assessment.questions.find(q => q.key === questionKey);
        const answer = this.assessment.answers.find(answer => answer.questionId === question?.id);

        const modal = await this.modalController.create({
            component: question?.answerType === 'time' ? PickerTimeOverlayComponent : PickerSelectOverlayComponent,
            componentProps: {
                question,
                answer
            },
            showBackdrop: true,
            backdropDismiss: true,
            cssClass: 'popover-modal'
        });

        modal.onDidDismiss().then(data => {
            if (data?.data) {
                const {questionKey, value} = data.data;
                if (questionKey && value !== null) {
                    this.sendAnswer(questionKey, value);
                }
            }
        });

        await modal.present();
    }

    private async openNumberPickerHaleo(questionKey: string) {

        const question = this.assessment.questions.find(q => q.key === questionKey);

        if (question) {
            const data = JSON.parse(question.options);
            const options = [];
            const increment = data.interval ? data.interval : 1;

            for (let i = data.min; i <= data.max; i += increment) {
                options.push({text: i + ' ' + data.unit, value: i});
            }

            const picker = await this.pickerCtrl.create({
                mode: 'ios',
                buttons: [{
                    text: this.translateService.instant('cancel'),
                    role: 'cancel'
                }, {text: this.translateService.instant('done')}],
                columns: [{name: 'number', options}]
            });

            await picker.present();
            await picker.onDidDismiss();
            const pickerValues = await picker.getColumn('number');

            if (pickerValues && pickerValues.selectedIndex !== undefined) {
                return pickerValues.options[pickerValues.selectedIndex].value;

            }
        }
    }

    private sendAnswer(questionKey: string, value: any) {

        this.loading = true;
        const question = this.assessment.questions.find(q => q.key === questionKey);
        const previousAnswerIndex = this.assessment.answers.findIndex(a => a.questionId == question?.id);

        if (question) {

            let answer: any;
            if (previousAnswerIndex >= 0) {
                answer = this.assessment.answers[previousAnswerIndex];
                this.assessment.answers[previousAnswerIndex].value = value;
            } else {

                answer = {
                    uuid: v4(),
                    value,
                    questionId: question.id
                };

                this.assessment.answers.push(answer);
            }

            this.assessmentService.sendAnswer(this.assessment, question, answer)
                .subscribe(answerUpdated => {

                    const answerToUpdate = this.assessment.answers.findIndex(a => a.uuid == answer.uuid);
                    this.assessment.answers[answerToUpdate].uuid = answerUpdated.uuid;

                    this.enableNext = true;
                    this.init();
                }, error => {

                    if (error.status === 422 && (error.error.errors['3_Negative_TST_Check'] || error.error.errors['1_TIB'])) {

                        this.enableNext = false;

                        if (error.error.errors['3_Negative_TST_Check'] && this.type === this.timeInBedType) {
                            this.type = this.sleepEfficiencyType;
                        }
                        if (error.error.errors['1_TIB'] && this.type === this.sleepEfficiencyType) {
                            this.type = this.timeInBedType;
                        }

                    } else {
                        this.enableNext = true;
                    }

                    this.init();
                });
        }
    }
}
